import {buildReturnTo} from '../../../lib/returnTo'
import type {PublicScheduleEvent} from '../../../services/public-schedule.service'
import {getPublicEventPath} from '@fitro/event-contract'
import {customerActivityDetailsHref} from '../../../lib/customerActivity'
import {calendarDayHref} from '../../../lib/calendarDayHref'
import {openRoutedModalOverBackground, shouldUseFullscreenModal} from '@fitro/modal-routing'
import {readModalViewportState} from '../../../hooks/useIsMobile'
import type {MouseEvent} from 'react'

export function getPublicEventDetailsHref(event: PublicScheduleEvent): string | null {
    return getPublicEventPath(event)
}

// A public Term is a one-off event or an Occurrence of a recurring class, each in its own calendar section.
type TermKind = {isCyclical: boolean}
type AppTerm = Pick<PublicScheduleEvent, 'id' | 'startDateTime' | 'timeZone'> & TermKind
const isOneOff = (event: TermKind) => event.isCyclical === false
const calendarSectionHref = (event: TermKind) =>
    isOneOff(event) ? '/calendar/events' : '/calendar'

/**
 * The Term in the app, opened straight from the studio's page: a class over the calendar on its
 * own day, an event on its page. The public Term already says which it is, so no handoff is needed.
 */
export function getAuthenticatedEventDetailsHref(event: AppTerm): string {
    return buildReturnTo(
        customerActivityDetailsHref(isOneOff(event) ? 'event' : 'group_class', event.id),
        isOneOff(event) ? calendarSectionHref(event) : calendarDayHref(event.startDateTime, event.timeZone),
    )
}

/**
 * On a wide screen a class opens as a window over the calendar of its day. From the studio's page
 * that takes one load of the calendar, which reopens the class over it, instead of first showing the
 * class as a page; `navigate` replaces that load with a router transition. Returns false where the
 * app shows the class as a page (phones, tablets, touch screens), for a one-off event, or when the
 * visit cannot be remembered: the caller opens the link.
 */
export function openClassOverCalendar(event: AppTerm, navigate?: (url: string) => void): boolean {
    if (isOneOff(event) || shouldUseFullscreenModal(readModalViewportState())) return false
    return openRoutedModalOverBackground('customer', calendarDayHref(event.startDateTime, event.timeZone), getAuthenticatedEventDetailsHref(event), navigate)
}

/** The click on a signed-in booking link of the studio's page; a new-tab click keeps the plain link. */
export function openTermFromStudioPage(click: MouseEvent<HTMLAnchorElement>, event: AppTerm) {
    if (click.defaultPrevented || click.button !== 0 || click.metaKey || click.ctrlKey || click.shiftKey || click.altKey) return
    if (openClassOverCalendar(event)) click.preventDefault()
}

export function getPublicEventBookingHandoffHref(
    event: Pick<PublicScheduleEvent, 'id'> & TermKind,
    returnTo: '/calendar' | '/calendar/events' = calendarSectionHref(event),
): string {
    return buildReturnTo(
        `/calendar/public-booking/${encodeURIComponent(event.id)}`,
        returnTo,
    )
}

const GUEST_CHECKOUT_PARAM = 'rezerwacja'
const GUEST_CHECKOUT_VALUE = 'bez-konta'

/** How a visitor books an event: as a guest, by signing in for its waitlist, with an account, or not at all. */
export type PublicEventBookingMode = 'guest' | 'waitlist' | 'account' | null

export function getPublicEventBookingMode(
    event: Pick<PublicScheduleEvent, 'availabilityStatus' | 'bookingOpen' | 'guestCheckoutAvailable' | 'publicCode' | 'waitlistAvailable'>,
    signedIn: boolean,
): PublicEventBookingMode {
    const bookable = event.bookingOpen !== false
        && (event.availabilityStatus === 'available' || event.availabilityStatus === 'last_spots')
    if (!bookable) {
        // A guest who finds a full Occurrence can sign in and join its waitlist.
        return !signedIn && event.availabilityStatus === 'full' && event.waitlistAvailable ? 'waitlist' : null
    }
    // Guest Checkout addresses an event by its public code; an Occurrence without one needs an account.
    return !signedIn && event.guestCheckoutAvailable === true && event.publicCode ? 'guest' : 'account'
}

/** The event details page with its Guest Checkout started on arrival. */
export function getPublicEventGuestCheckoutHref(event: PublicScheduleEvent): string | null {
    const detailsHref = getPublicEventDetailsHref(event)
    return detailsHref ? `${detailsHref}?${GUEST_CHECKOUT_PARAM}=${GUEST_CHECKOUT_VALUE}` : null
}

/** Leaving the guest form drops the request from the address, so a reload does not reopen it. */
export function clearPublicEventGuestCheckoutRequest(): void {
    const url = new URL(window.location.href)
    if (url.searchParams.get(GUEST_CHECKOUT_PARAM) !== GUEST_CHECKOUT_VALUE) return
    url.searchParams.delete(GUEST_CHECKOUT_PARAM)
    window.history.replaceState(window.history.state, '', url)
}

export function isPublicEventGuestCheckoutRequested(search: string): boolean {
    return new URLSearchParams(search).get(GUEST_CHECKOUT_PARAM) === GUEST_CHECKOUT_VALUE
}

